import Title_ru_RU              from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class        from '@/core/misc/decorators/Title_ru_RU_Class';

import TYPES                    from '@/core/services/Changelogs/types';

import AbstractDocumentPosition from '@/domains/Abstract/AbstractDocumentPosition';

import type ShipmentToBuyer     from '@/domains/ShipmentsToBuyer/ShipmentToBuyer';
import Core                     from '@/core/Core';
import Price                    from '@/core/types/Price';
import Sum                      from '@/core/types/Sum';
import Multiplier               from '@/core/types/Multiplier';
import Currency                 from "@/domains/Currencies/Currency";
import CurrencyRate             from "@/core/types/CurrencyRate";
import CurrenciesService        from '@/domains/Currencies/CurrenciesService';
import VatRate                  from "@/core/types/VatRate";
/**
 * Позиция реализации покупателю.
 * Read-only: генерируется на беке из позиций отгрузки поставщика,
 * суммы денормализованы в валюте сделки и валюте учёта.
 */
@Title_ru_RU_Class('Позиция реализации покупателю')
class ShipmentToBuyerPosition extends AbstractDocumentPosition {

  /** Родительский документ — обратную ссылку ставит родитель после гидрации */
  protected _shipmentToBuyer: ShipmentToBuyer;

  // ---- Валюта сделки ----

  protected _orderCurrencyCode: string | null;
  protected _orderCurrency: Currency | null;

  @Title_ru_RU('Цена', TYPES.PRICE)
  public price: Price;

  @Title_ru_RU('Ставка НДС', TYPES.NUMBER)
  public vatRate: VatRate;

  public isVatIncluded: boolean;

  @Title_ru_RU('Полная цена', TYPES.PRICE)
  public priceFull: Price;

  @Title_ru_RU('Сумма', TYPES.SUM)
  public sum: Sum;

  @Title_ru_RU('Полная сумма', TYPES.SUM)
  public sumFull: Sum;

  @Title_ru_RU('Сумма НДС', TYPES.SUM)
  public vatSum: Sum;

  // ---- Валюта учёта ----

  protected _accountingCurrencyCode: string | null;
  protected _accountingCurrency: Currency | null;

  private _accountingCurrencyRate: CurrencyRate;

  @Title_ru_RU('Цена в валюте учёта', TYPES.PRICE)
  public accountingPrice: Price;

  @Title_ru_RU('Полная цена в валюте учёта', TYPES.PRICE)
  public accountingPriceFull: Price;

  @Title_ru_RU('Сумма в валюте учёта', TYPES.SUM)
  public accountingSum: Sum;

  @Title_ru_RU('Полная сумма в валюте учёта', TYPES.SUM)
  public accountingSumFull: Sum;

  @Title_ru_RU('Сумма НДС в валюте учёта', TYPES.SUM)
  public accountingVatSum: Sum;

  @Title_ru_RU('Тех наценка', TYPES.SUM)
  public sftPriceMod: Multiplier;


  /* ────────────────────────────────────────
     Геттеры / сеттеры
     ──────────────────────────────────────── */

  // ---- Родительский документ ----

  get shipmentToBuyer(): ShipmentToBuyer { return this._shipmentToBuyer; }
  set shipmentToBuyer(value: ShipmentToBuyer) { this._shipmentToBuyer = value; }

  // ---- Валюта сделки — код ведущий, объект из словаря ----

  get orderCurrencyCode(): string | null { return this._orderCurrencyCode; }
  set orderCurrencyCode(value: string | null) {
    this._orderCurrencyCode = value;

    if (this._orderCurrencyCode !== null && this._orderCurrencyCode !== undefined) {
      this._orderCurrency = CurrenciesService.currencies.get(this._orderCurrencyCode) ?? null;
    } else {
      this._orderCurrency = null;
    }
  }

  get orderCurrency(): Currency | null { return this._orderCurrency; }
  set orderCurrency(value: Currency | null) {
    /** Ведущий — код: его сеттер подтянет объект из словаря. */
    this.orderCurrencyCode = value?.code ?? null;
  }

  // ---- Валюта учёта — код ведущий, объект из словаря ----

  get accountingCurrencyCode(): string | null { return this._accountingCurrencyCode; }
  set accountingCurrencyCode(value: string | null) {
    this._accountingCurrencyCode = value;

    if (this._accountingCurrencyCode !== null && this._accountingCurrencyCode !== undefined) {
      this._accountingCurrency = CurrenciesService.currencies.get(this._accountingCurrencyCode) ?? null;
    } else {
      this._accountingCurrency = null;
    }
  }

  get accountingCurrency(): Currency | null { return this._accountingCurrency; }
  set accountingCurrency(value: Currency | null) {
    /** Ведущий — код: его сеттер подтянет объект из словаря. */
    this.accountingCurrencyCode = value?.code ?? null;
  }

  get accountingCurrencyRate(): CurrencyRate { return this._accountingCurrencyRate; }
  set accountingCurrencyRate(value: CurrencyRate) { this._accountingCurrencyRate = value; }

  /* ────────────────────────────────────────
     fromServer
     ──────────────────────────────────────── */

  static fromServer(data: any, position: ShipmentToBuyerPosition = new ShipmentToBuyerPosition()): ShipmentToBuyerPosition {
    /* orderPosition, qty, product, unit гидрируются в AbstractDocumentPosition.fromServer */
    super.fromServer(data, position);

    // ---- Валюта сделки ----
    position.orderCurrencyCode = data.orderCurrencyCode;

    position.vatRate   = Core.utils.hydrate(data.vatRate, VatRate.fromServer)!;
    position.price     = Core.utils.hydrate(data.price, Price.fromServer)!;
    position.priceFull = Core.utils.hydrate(data.priceFull, Price.fromServer)!;
    position.sum       = Core.utils.hydrate(data.sum, Sum.fromServer)!;
    position.sumFull   = Core.utils.hydrate(data.sumFull, Sum.fromServer)!;
    position.vatSum    = Core.utils.hydrate(data.vatSum, Sum.fromServer)!;


    // ---- Валюта учёта ----

    position.accountingCurrencyCode = data.accountingCurrencyCode;
    position.accountingCurrencyRate = Core.utils.hydrate(data.accountingCurrencyRate, CurrencyRate.fromServer)!;

    position.accountingPrice     = Core.utils.hydrate(data.accountingPrice, Price.fromServer)!;
    position.accountingPriceFull = Core.utils.hydrate(data.accountingPriceFull, Price.fromServer)!;
    position.accountingSum       = Core.utils.hydrate(data.accountingSum, Sum.fromServer)!;
    position.accountingSumFull   = Core.utils.hydrate(data.accountingSumFull, Sum.fromServer)!;
    position.accountingVatSum    = Core.utils.hydrate(data.accountingVatSum, Sum.fromServer)!;
    position.sftPriceMod         = Core.utils.hydrate(data.sftPriceMod, Multiplier.fromServer)!;

    return position;
  }
}

export default ShipmentToBuyerPosition;
